/**
 * manage.js
 */

import React from 'react';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useMediaQuery, Container, Box, Link } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Types } from 'utils/constants';
import useMounted from 'utils/use-mounted';
import { UserContext } from 'contexts/user/user-context';
import { UserDataContext } from 'contexts/user-data/user-data-context';
import { AppBarContext } from 'contexts/app-bar/app-bar-context';
import useRecordStore from 'contexts/record-store';
import PageTitle from 'components/page-title';
import FeatureBox from 'components/feature-box';
import IconTitle from 'components/icon-title';
import CaptionedSvg from 'components/captioned-svg';
import Icon from 'components/icon';
import DropMenu from 'components/drop-menu';
import Button from 'components/button';
import ConfirmDialog from 'components/confirm-dialog';
import BarButton from 'components/layout/app-bar/bar-button';
import ManageSupervisors from './supervisors';
import ManageHandlers from './handlers';
import ManagePending from './pending';
import ManageManagers from './managers';
import AddUserDialog from './add-user-dialog';
import { ReactComponent as ConnectionsSvg } from 'assets/images/connections.svg';


const useStyles = makeStyles(theme => ({
  root: {
    paddingTop: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      paddingTop: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  section: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(4),
    '& .captioned-svg': {
      maxWidth: 500
    }
  },
  list: {
    marginTop: theme.spacing(2),
    borderRadius: 4,
    overflow: 'hidden',
    '& .fake-header': {
      backgroundColor: theme.palette.comp.dataGrid.headerBackground,
      paddingTop: theme.spacing(1),
      paddingLeft: 250,
      fontSize: theme.typography.sizes[1],
      fontWeight: 500,
      lineHeight: '12px',
      '& span': {
        position: 'relative',
        bottom: -4,
        zIndex: 99
      }
    },
    '& .MuiDataGrid-root': {
      border: 'none',
    },
    '& .MuiDataGrid-columnHeaders': {
      '& .MuiDataGrid-columnHeaderTitle': {
        fontSize: theme.typography.sizes[1],
        lineHeight: '14px',
      },
      '& .no-padding': {
        padding: 0,
      },
      '& .small-header .MuiDataGrid-columnHeaderTitle': {
        fontSize: theme.typography.sizes[0],
        lineHeight: '12px',
      }

    },
    '& .chart-cell': {
      paddingRight: `${theme.spacing(2)} !important`
    },
    '& .simple-count-cell > *': {
      transform: 'translateX(-12px)'
    },
    '& ul': {
      padding: 0
    }
  },
  handlers: {

  },
  supervisors: {
    maxWidth: 475,
    [theme.breakpoints.down('sm')]: {
      maxWidth: 'unset'
    }
  },
  pending: {
    maxWidth: 850,
    '& .request-type-cell': {
      lineHeight: '16px !important',
    },
    '& .accept-button': {
      minWidth: 'auto',
      marginRight: theme.spacing(1),
    },
    [theme.breakpoints.down('sm')]: {
      maxWidth: 'unset'
    }
  },
  managers: {
    maxWidth: 620
  }
}));


// TODO: this has gotten too bulky and manages too many lists & state for various lists. 
// the next task to do anything significant in here should refactor into supervisor/handler/pending subsections 
// that manage their own lists and state to simplify this component.
export default function Manage(props) {
  const classes = useStyles();
  const { user } = React.useContext(UserContext);
  const { userData, setUserData } = React.useContext(UserDataContext);
  const { setAppBar } = React.useContext(AppBarContext);
  const isMounted = useMounted();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  const recordStore = useRecordStore();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = React.useState(false);
  // super stuff
  const [directSupervisors, setDirectSupervisors] = React.useState();
  const [indirectSupervisors, setIndirectSupervisors] = React.useState();
  // handler stuff
  const [allHandlers, setAllHandlers] = React.useState([]);
  const [countInactiveHandlers, setCountInactiveHandlers] = React.useState([]);
  const [supervisorIdFilter, setSupervisorIdFilter] = React.useState(0);
  const [isShowingInactive, setIsShowingInactive] = React.useState(userData.manageDoShowInactive !== undefined
    ? userData.manageDoShowInactive
    : true);
  const [isAllExpanded, setIsAllExpanded] = React.useState(userData.manageDoExpandAll !== undefined
    ? userData.manageDoExpandAll
    : true);
  // pending stuff
  const [pendingUsers, setPendingUsers] = React.useState();
  // manager stuff
  const [managers, setManagers] = React.useState();
  // new user stuff
  const [isAddOpen, setIsAddOpen] = React.useState(false);
  const [newUserManagerRole, setNewUserManagerRole] = React.useState();
  const [newUserError, setNewUserError] = React.useState();
  const newUserEmailRef = React.useRef();
  // remove user stuff
  const [removeAction, setRemoveAction] = React.useState();
  const [confirmRemoveAction, setConfirmRemoveAction] = React.useState();


  const processData = React.useCallback((data) => {
    const statsSum = (statsArray) => {
      return statsArray.reduce((t, s) => t + s.value, 0);
    }

    setIndirectSupervisors(data.indirectSupervisors?.filter(s => s.handlers.length > 0) || []);
    setDirectSupervisors(data.directSupervisors || []);
    setPendingUsers(data.pending);
    setManagers(data.managingSupervisors);
    
    // pre-process handler data to generate meta stats for sorting by-month columns
    setAllHandlers(data.handlers.map(h => {
      return {
        ...h,
        trainingByMonth: statsSum(h.trainingStats),
        deploymentsByMonth: h.deploymentStats ? statsSum(h.deploymentStats) : 0,
      };
    }));
  }, []);


  const addMemberDropdown = React.useMemo(() => {
    const menuItems = user.role.isSupervisor
      ? [{
          id: Types.Role.SUPERVISOR,
          icon: 'handler',
          name: 'Invite Handler ...',
        }, {
          id: Types.Role.MANAGER,
          icon: 'supervisor',
          name: 'Invite Supervisor ...',
        }, {
          id: 'divider',
        }, {
          id: 'new-handler',
          icon: 'group-add-members',
          name: 'Signup New Member',
          endIcon: 'open-in-tab',
          component: Link,
          href: 'https://packtrackapp.com/get-started/',
          target: '_blank',
          color: 'inherit',
        }]
      : [{
          id: Types.Role.TRAINER,
          icon: 'trainer',
          name: 'Invite Handler ...',
        }];

    const label = (isOpen, onClick, arrowIcon) => isMobile 
      ? <BarButton 
        data-testid='add-managed-user-button'
        onClick={onClick}
        children={<Icon type='add' />}
      />
      : <Button
        data-testid='add-managed-user-button'
        variant="contained"
        aria-label="add managed user"
        startIcon={<Icon type='add' />}
        endIcon={arrowIcon}
        onClick={onClick}
        children={<span>Member</span>}
      />;


    return <DropMenu
      id='add-managed-user-menu'
      items={menuItems}
      label={label}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      onSelect={item => {
        if (item.id !== 'new-handler') {
          setNewUserManagerRole(item.id);
          setIsAddOpen(true);
          setTimeout(() => {
            if (newUserEmailRef.current) {
              newUserEmailRef.current.focus();
            }
          });
        }
      }}
    />;
  }, [isMobile, user]);


  // init page
  React.useEffect(() => {
    document.title = 'PACKTRACK - Manage';
    setAppBar('Manage', [addMemberDropdown]);
  }, [setAppBar, addMemberDropdown]);


  // init - data request
  React.useEffect(() => {
    (async() => {
      setLoading(true);
      const response = await axios.get('/manage');
      if (response.status === 200 && isMounted.current) {
        processData(response.data);
      }      
      setLoading(false);
    })();
  // isMounted is a ref
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processData]);


  const onChangeShowInactive = (val) => {
    setIsShowingInactive(val);
    if (userData.manageDoShowInactive !== val) {
      setUserData({ manageDoShowInactive: val });
    }
  }


  const onChangeExpandAll = (val) => {
    setIsAllExpanded(val);
    if (userData.manageDoExpandAll !== val) {
      setUserData({ manageDoExpandAll: val });
    }
  }


  const onAdd = async (email, role) => {
    setLoading(true);

    try {
      const params = { role, managerEmail: user.email, userEmail: email, context: 'manage' };
      const response = await axios.post('/manage/request', params, { bypassErrorHandling: true });
      if (response.status === 200) {
        const data = response.data;
        setNewUserError();
        setIsAddOpen(false);
        recordStore.reset();

        let addedHandler;

        if (email === user.email) {
          processData(data);

          if(isShowingInactive === false) {
            setIsShowingInactive(true);
          }

          addedHandler = data.handlers?.filter(handler => handler.email === email)[0];
        } else {
          addedHandler = data.pending?.filter(handler => handler.email === email)[0];
          setPendingUsers(data.pending);
        }

        enqueueSnackbar(`Request sent to ${addedHandler ? addedHandler.name : email}`, {
          variant: 'info',
        });

        return true;
      }
    } catch (e) {
      setNewUserError(
        e.response?.data?.Message
        || e.response?.data 
        || e.response?.statusText 
        || 'Unknown error adding managed user'
      );
    } finally {
      setLoading(false);
    }
  }


  const onRemove = React.useCallback(async (role, managerId, userId, userInfo) => {
    setRemoveAction(userInfo);

    if (await confirmRemoveAction()) {
      setLoading(true);
      const params = { role, managerId, userId, context: 'manage' };
      const response = await axios.delete('/manage/member', { params });
      if (response.status === 200) {
        processData(response.data);
        recordStore.reset();
      }
      setLoading(false);
    }
  // recordStore is hookstate obj
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmRemoveAction, processData]);


  const onRemoveRequest = React.useCallback(async (code, userId, userInfo) => {
    setRemoveAction(userInfo);
    if (await confirmRemoveAction()) {
      setLoading(true);
      const response = await axios.delete(`/manage/request/${code}`);
      if (response.status === 200) {
        setPendingUsers(p => p.filter(u => u.user.id !== userId || u.code !== code));
      }
      setLoading(false);
    }
  }, [confirmRemoveAction]);


  const onAccept = React.useCallback(async (request) => {
    setLoading(true);
    const response = await axios.post(`/manage/request/${request.code}`, { context: 'manage' });
    if (response.status === 200) {
      recordStore.reset();
      processData(response.data);
      enqueueSnackbar(`Management request accepted`, { variant: 'info' });
    }
    setLoading(false);
  // recordStore is hookstate obj
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enqueueSnackbar, processData]);


  const filteredHandlers = React.useMemo(() => {
    let handlers = allHandlers;

    if (supervisorIdFilter > 0) {
      handlers = handlers.filter(h => h.supervisors.find(s => s.id === supervisorIdFilter));
    }

    setCountInactiveHandlers(handlers.reduce(
      (count, handler) =>
        count += handler.isActive ? 0 : 1,
      0
    ));

    if (!isShowingInactive) {
      handlers = handlers.filter(h => h.isActive);
    }

    return handlers;
  }, [allHandlers, supervisorIdFilter, isShowingInactive]);


  const handlerList = React.useMemo(() => {
    return <ManageHandlers
      data={filteredHandlers}
      onRemove={onRemove}
      isAllExpanded={isAllExpanded}
    />;
  }, [filteredHandlers, onRemove, isAllExpanded]);


  const supervisorList = React.useMemo(() => (
    <ManageSupervisors
      data={directSupervisors}
      onRemove={onRemove}
    />
  ), [directSupervisors, onRemove]);


  const pendingList = React.useMemo(() => (
    <ManagePending
      requests={pendingUsers}
      onRemove={onRemoveRequest}
      onAccept={onAccept}
    />
  ), [pendingUsers, onRemoveRequest, onAccept]);


  const managerList = React.useMemo(() => (
    <ManageManagers
      managers={managers}
      onRemove={onRemove}
    />
  ), [managers, onRemove]);


  return (
    <>
      <Container data-testid='manage-page' className={classes.root}>

        {/* desktop title */}
        {!isMobile &&
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <PageTitle iconName='group' title='Manage' />
            <div className='flex-hc' style={{ justifyContent: 'flex-end' }}>
              {addMemberDropdown}
            </div>
          </Box>
        }

        <FeatureBox style={{ minHeight: 140 }} loading={loading}>
          {directSupervisors && allHandlers &&
          <>
            {/* MANAGING SUPERS */}
            {managers?.length > 0 &&
            <div data-testid='managing-supervisors-section'>
              <div className={classes.section}>
                <IconTitle
                  iconType='manage-managers'
                  title='Management Access'
                  subtitle='These are the supervisors who supervise you. They have access to the records of all the handlers that you supervise.'
                />
                <div className={`${classes.list} ${classes.managers}`}>
                  {managerList}
                </div>
              </div>
            </div>}

            {/* SUPERVISORS */}
            {user.role.isSupervisor &&
            <div data-testid='supervisors-section'>
              <div className={classes.section}>
                <IconTitle
                  iconType='manage-supervisors'
                  title='Managed Supervisors'
                  subtitle={<>
                    {directSupervisors.length > 1 && <>Invite other supervisors to be managed. This gives you access to any handlers or supervisors that they themselves supervise.</>}
                    {directSupervisors.length <= 1 && <>You haven't added any managed supervisors. Use the <b>+ Member</b> button to invite a supervisor to be managed.</>}
                  </>}
                />
                {directSupervisors.length > 1 &&
                <div className={`${classes.list} ${classes.supervisors}`}>{supervisorList}</div>}
                
              </div>
            </div>}

            {/* HANDLERS */}
            <div data-testid='handlers-section'>
              <div className={classes.section}>
                <IconTitle
                  iconType='manage-handlers'
                  title='Handlers'
                  subtitle={<>
                    {user.role.isSupervisor && <>These are the handlers that you supervise. This includes handlers that you directly supervise as well as handlers that are supervised by one of your managed supervisors.</>}
                    {user.role.isTrainer && <>These are the handlers that you train.</>}
                  </>}
                  actions={isMobile
                    ? (
                      <DropMenu
                        id='mobile-handler-list-menu'
                        label={<span className='flex-hc'><Icon type='settings' /><Icon type='arrow-down' /></span>}
                        items={[{
                          id: 'toggle-inactive',
                          icon: isShowingInactive ? 'visibility-off' : 'visibility-on',
                          name: `${isShowingInactive ? 'Hide' : 'Show'} Inactive (${countInactiveHandlers})`,
                          onSelect: () => onChangeShowInactive(!isShowingInactive)
                        }, {
                          id: 'toggle-expansion',
                          icon: isAllExpanded ? 'arrow-up-bold' : 'arrow-down-bold',
                          name: `${isAllExpanded ? 'Collapse' : 'Expand'} All`,
                          onSelect: () => onChangeExpandAll(!isAllExpanded)
                        }]}
                      />
                    )
                    : allHandlers.length > 0 && (
                      <DropMenu
                        id='desktop-handler-list-menu'
                        label={<Button 
                          variant='contained'
                          color='primary'
                          children='Filters'
                          startIcon={<Icon type='settings' />}
                          endIcon={<Icon type='arrow-down' />}
                        />}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right'
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'right'
                        }}
                        items={[{
                          id: 'toggle-inactive',
                          icon: isShowingInactive ? 'visibility-off' : 'visibility-on',
                          name: `${isShowingInactive ? 'Hide' : 'Show'} Inactive (${countInactiveHandlers})`,
                          onSelect: () => onChangeShowInactive(!isShowingInactive)
                        }, ...(user.role.isTrainer || indirectSupervisors.length <= 1
                          ? []
                          : [{
                            id: 'filter-by-super',
                            icon: 'supervisor',
                            name: 'Supervised By',
                            onSelect: (s) => setSupervisorIdFilter(s.id),
                            items: [
                              { id: '', name: 'All', active: !supervisorIdFilter },
                              ...indirectSupervisors.map(s => ({
                                id: s.id,
                                name: `${s.id === user.id ? 'Me' : s.name} (${s.handlers.length})`,
                                active: supervisorIdFilter === s.id
                              }))
                            ]
                          }])
                        ]}
                      />
                    ) 
                  }
                />
                {allHandlers.length > 0 &&
                <div className={`${classes.list} ${classes.handlers}`}>{handlerList}</div>}
                {allHandlers.length === 0 &&
                <CaptionedSvg
                  image={<ConnectionsSvg />}
                  text={<>You aren't {user.role.isSupervisor ? 'supervising' : 'training'} any handlers. Use the <b>+ Member</b> button to invite a handler that you manage.</>}
                />}
              </div>
            </div>

            {/* PENDING */}
            {pendingUsers?.length > 0 &&
            <div data-testid='pending-requests-section'>
              <div className={classes.section}>
                <IconTitle
                  iconType='manage-pending'
                  title='Pending Requests'
                  subtitle='Any outstanding management requests which have been sent or received are listed below.'
                />
                <div className={`${classes.list} ${classes.pending}`}>
                  {pendingList}
                </div>
              </div>
            </div>}
          </>}

        </FeatureBox>
      </Container>


      {/* add user dialog */}
      <AddUserDialog
        managerRole={newUserManagerRole}
        error={newUserError}
        isOpen={isAddOpen}
        onOk={onAdd}
        onClose={() => {
          setIsAddOpen(false);
          setNewUserError();
        }}
      />


      {/* Remove various confirmation dialog */}
      <ConfirmDialog
        title={`Confirm Remove ${removeAction?.userType}`}
        titleStyle="warning"
        titleIcon='warning'
        content={`Are you sure you want to ${removeAction?.text}?`}
        setConfirm={setConfirmRemoveAction}
        customActions={[{
          label: 'Cancel',
          value: false,
          isNotPriority: true,
        }, {
          label: 'Remove',
          value: true,
        }]}
      />
    </>
  );
}